/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatStr, SyntaxTreeRecognize, TokenUtil } from '@patsnap/query-translator'
import {
  E_QUERY_ITEM_CONDITION,
  IQuery,
  IQueryDataType,
  IQueryItem,
  IQueryItemCustomEntity,
  IQueryItemEntity,
  IQueryValue,
  ISearchCollapse,
  ISearchParams,
  ISort,
} from '@patsnap/synapse_common_interface'
import { I18nLang } from '@pharmsnap/shared/i18n'
import { useGlobalCalculateRights } from '@pharmsnap/shared/src/composition/useCalculateRights'
import { trackingEvent } from '@pharmsnap/shared/src/features/tracking'
import { IBAcTag, IListItem, ISearchHistoryFromModule, ISelectedLabel } from '@pharmsnap/shared/src/types'
import { getCurrentInstance, reactive } from '@vue/composition-api'
import { useLocale } from 'pharmsnapMF_shared/composition'
import {
  AllFinderFieldType,
  ClinicalFieldType,
  ClinicalResultFieldType,
  convert2EntityQueryItem,
  convert2NumberRangeQueryItem,
  convert2RangeQueryItem,
  convert2TextQueryItem,
  convert2TextQueryItemForATC,
  convertClinicalResultOutcome2QueryItem,
  convertDateRange2NumberRangeQueryItem,
  convertItem2Tag,
  convertPatentOrg2QueryItem,
  DataTagKey,
  DrugDealFieldType,
  DrugFinderFieldType,
  FinderFieldType,
  FinderInputKey,
  generateAllTag,
  getStringLength,
  handleBrowseSubmit,
  ILogicKey,
  PatentFieldType,
  showSingleToast,
  tagData,
  TranslationalMedicineFieldType,
} from 'pharmsnapMF_shared/utils'
import { useFinderSearch } from '../components/finder/composition/useFinderSearch'
import { checkExist, getRemoveResult, isStringQueryItem, transDefaultSyntaxTreeQuery } from '../components/finder/util'
import { CommonListTabSetting } from '../features/commonlist-tab-setting'
import { E_ROUTER_NAME, E_ROUTER_QUERY } from '../types/enum'
import { handleViewDetail as viewDetail } from '../utils/business/autocomplete'
import { useFinderLimit } from './useFinderLimit'

type IFinderQueryType = Extract<
  IQueryDataType,
  'drug' | 'clinical_trial' | 'paper' | 'patent' | 'clinical_trial_result' | 'drug_deal' | 'translational_medicine'
>

const RouteConfig: Record<IFinderQueryType, { fromModule: ISearchHistoryFromModule; route: E_ROUTER_NAME; routeParam: E_ROUTER_QUERY }> = {
  drug: {
    fromModule: 'Drug Finder',
    route: E_ROUTER_NAME.DRUG_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  clinical_trial: {
    fromModule: 'Clinical Progress',
    route: E_ROUTER_NAME.CLINICAL_PROGRESS_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  paper: {
    fromModule: 'Literature',
    route: E_ROUTER_NAME.LITERATURE_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  patent: {
    fromModule: 'Patent',
    route: E_ROUTER_NAME.PATENT_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  clinical_trial_result: {
    fromModule: 'Clinical Result Finder',
    route: E_ROUTER_NAME.CLINICAL_RESULT_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  drug_deal: {
    fromModule: 'Drug Deal Finder',
    route: E_ROUTER_NAME.DRUG_DEAL_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
  translational_medicine: {
    fromModule: 'Translational Medicine Finder',
    route: E_ROUTER_NAME.TRANSLATIONAL_MEDICINE_LIST,
    routeParam: E_ROUTER_QUERY.QUERY_ID,
  },
}

const ConvertValueConfig: Record<IFinderQueryType, { convertValue: any }> = {
  drug: {
    convertValue: (items: IBAcTag[] | string[], type: DrugFinderFieldType, locale: I18nLang): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items as IBAcTag[], type)
        case 'text':
          return convert2TextQueryItem(items as IBAcTag[], locale)
        case 'atc':
          return convert2TextQueryItemForATC(items as IBAcTag[])
        case 'dateRange':
          if (items.some((item) => typeof item === 'string')) {
            return convert2RangeQueryItem(items as string[])
          }
          return convertDateRange2NumberRangeQueryItem(items as IBAcTag[], undefined, true)
      }
    },
  },
  clinical_trial: {
    convertValue: (items: IBAcTag[], type: ClinicalFieldType, locale: I18nLang): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items, type)
        case 'text':
          return convert2TextQueryItem(items, locale)
        case 'dateRange':
          return convertDateRange2NumberRangeQueryItem(items, undefined, true)
        case 'numberRange':
          return convert2NumberRangeQueryItem(items)
      }
    },
  },
  paper: {
    convertValue: (items: IBAcTag[], type: FinderFieldType, locale: I18nLang): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items as IBAcTag[], type)
        case 'text':
          return convert2TextQueryItem(items as IBAcTag[], locale)
        case 'dateRange':
          return convertDateRange2NumberRangeQueryItem(items, 'YYYY', false, {
            include_lower: true,
            include_upper: true,
          })
      }
    },
  },
  patent: {
    convertValue: (items: IBAcTag[], type: PatentFieldType, locale: I18nLang, assigneeIncludeOriginal?: boolean): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
          return convert2EntityQueryItem(items, type)
        case 'organization':
          return convertPatentOrg2QueryItem(items, false, assigneeIncludeOriginal)
        case 'text':
          return convert2TextQueryItem(items, locale)
        case 'dateRange':
          return convertDateRange2NumberRangeQueryItem(items, 'YYYYMMDD', false, {
            include_lower: true,
            include_upper: true,
          })
      }
    },
  },
  clinical_trial_result: {
    convertValue: (
      items: IBAcTag[] | string[],
      type: ClinicalResultFieldType,
      locale: I18nLang,
      _assigneeIncludeOriginal?: boolean,
      isPrimaryEndpoint?: boolean
    ): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items as IBAcTag[], type)
        case 'meetings':
          return convert2TextQueryItem(items as IBAcTag[], locale, true)
        case 'text':
          return convert2TextQueryItem(items as IBAcTag[], locale)
        case 'dateRange':
          return convert2RangeQueryItem(items as string[])
        case 'numberRange':
          return convert2NumberRangeQueryItem(items as IBAcTag[])
        case 'outcome':
          return convertClinicalResultOutcome2QueryItem(items as IBAcTag[], isPrimaryEndpoint)
      }
    },
  },
  drug_deal: {
    convertValue: (items: IBAcTag[], type: DrugDealFieldType, locale: I18nLang): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items as IBAcTag[], type)
        case 'text':
          return convert2TextQueryItem(items as IBAcTag[], locale)
        case 'dateRange':
          return convertDateRange2NumberRangeQueryItem(items, undefined, true)
        case 'numberRange':
          return convert2NumberRangeQueryItem(items as IBAcTag[])
      }
    },
  },
  translational_medicine: {
    convertValue: (items: IBAcTag[], type: TranslationalMedicineFieldType, locale: I18nLang): IQueryValue[] => {
      switch (type) {
        case 'disease':
        case 'drug':
        case 'target':
        case 'organization':
          return convert2EntityQueryItem(items as IBAcTag[], type)
        case 'text':
          return convert2TextQueryItem(items as IBAcTag[], locale)
        case 'meetings':
          return convert2TextQueryItem(items as IBAcTag[], locale, true)
        case 'dateRange':
          return convertDateRange2NumberRangeQueryItem(items, 'YYYYMMDD', false, {
            include_lower: true,
            include_upper: true,
          })
      }
    },
  },
}

export function useFinderComp(options?: {
  initLogicConfig?: Partial<Record<ILogicKey, E_QUERY_ITEM_CONDITION>>
  supportChangeLogics?: Array<ILogicKey>
  customSortFn?: (query: IQuery) => ISort[] | undefined
}) {
  const { initLogicConfig = {}, supportChangeLogics = [] } = options || {}
  const { isCN, locale, fieldLang } = useLocale()
  const { getQueryIdByQuery } = useFinderSearch()
  const { checkFinderSearchIsOverLimit } = useFinderLimit()
  const ins = getCurrentInstance()

  const { handleUpdateRights } = useGlobalCalculateRights()

  const logicConfig = reactive<Record<ILogicKey, E_QUERY_ITEM_CONDITION>>({
    targetItems: E_QUERY_ITEM_CONDITION.ALL,
    rnaTargetItems: E_QUERY_ITEM_CONDITION.ALL,
    adcTargetItems: E_QUERY_ITEM_CONDITION.ALL,
    indicationItems: E_QUERY_ITEM_CONDITION.ANY,
    allFieldItems: E_QUERY_ITEM_CONDITION.ANY,
    experimentalDrugItems: E_QUERY_ITEM_CONDITION.ALL,
    clinicalDrugItems: E_QUERY_ITEM_CONDITION.ALL,
    translationalMedicineDrugItems: E_QUERY_ITEM_CONDITION.ALL,
    drugItems: E_QUERY_ITEM_CONDITION.ALL,
    attributeItems: E_QUERY_ITEM_CONDITION.ALL,
    targetedMoietyTargetItems: E_QUERY_ITEM_CONDITION.ALL,
    payloadTargetItems: E_QUERY_ITEM_CONDITION.ALL,
    ...initLogicConfig,
  })

  const action = {
    changeInputVal: <T extends FinderInputKey>(data: any, key: T, item: IListItem, isKeyword = false) => {
      const isExist = checkExist(data[key], item, isCN.value)
      console.log({
        isExist,
        item,
        key,
      })
      if (isExist) {
        showSingleToast({
          message: ins?.proxy.$t('common.excitedItem') as string,
          type: 'info',
        })
      } else {
        const tag = convertItem2Tag(key, item, isKeyword)
        data[key].push(tag)
      }
    },
    handleChangeLogicVal: (key: string, val: E_QUERY_ITEM_CONDITION) => {
      if (supportChangeLogics.includes(key as ILogicKey) && key in logicConfig) {
        logicConfig[key as ILogicKey] = val
      }
    },
    handleViewDetail: (item: IListItem) => {
      ins?.proxy.$router && viewDetail(item, ins.proxy.$router)
    },
    closeTag: <T>(data: any, key: T, item: IBAcTag) => {
      data[key] = getRemoveResult(data[key], item, isCN.value)
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleInputKeyDown: <T>(data: any, key: T, e: KeyboardEvent, keyword?: string) => {
      const { key: keyboardKey } = e
      if (keyboardKey === 'Backspace') {
        if ((e.target as HTMLInputElement).value.length === 0) {
          data[key].pop()
        }
      }
    },
    transKeyword2Tag: <T extends FinderInputKey>(data: any, key: T, keyword: string, isKeyword = false) => {
      const trimKeywords = keyword.trim()
      if (trimKeywords && getStringLength(trimKeywords) >= 2) {
        action.changeInputVal<T>(
          data,
          key,
          {
            id: '',
            display_name_en: trimKeywords,
            display_name_cn: trimKeywords,
          },
          isKeyword
        )
      }
    },
    handleBrowseRes: <T extends FinderInputKey>(data: any, key: T, list: ISelectedLabel[]) => {
      data[key] = handleBrowseSubmit(key, list)
    },
    addLoadedData: <T extends FinderInputKey>(data: any, key: T, item: IListItem, condition = E_QUERY_ITEM_CONDITION.ANY) => {
      const isExist = checkExist(data[key], item, isCN.value)
      if (isExist) {
        showSingleToast({
          message: ins?.proxy.$t('common.excitedItem') as string,
          type: 'info',
        })
      } else {
        data[key].push({ ...item })

        const logicKeys = Object.keys(logicConfig)
        if (logicKeys.includes(key)) {
          logicConfig[key as ILogicKey] = condition
        }
      }
    },
    normalTags2QueryString: (data: any, key: 'allFieldItems') => {
      const logicVal = logicConfig[key]
      let logic = 'or'
      if (logicVal === E_QUERY_ITEM_CONDITION.ALL) {
        logic = 'and'
      }
      return ((data[key] as IBAcTag[]) || [])
        .map((o) => {
          const val = o[`display_name_${fieldLang.value}`]
          const reg = /\s/
          return reg.test(val) ? `"${val}"` : val
        })
        .join(` ${logic} `)
    },
    handleEditQueryConfirm: (data: any, key: 'allFieldItems', params: { query?: IQuery; source: string }, fields?: string[]) => {
      const { query, source } = params
      if (!query) return
      if (query.type === 'group') {
        query.source = source.trim()
      }
      transDefaultSyntaxTreeQuery(query, fields)
      data[key] = [
        {
          id: '',
          display_name_en: source,
          display_name_cn: source,
          searchQuery: query,
        } as IListItem,
      ]
    },
    handlePreviewSearch: async (
      must: IQueryItem[],
      dataType: IFinderQueryType,
      collapse?: ISearchCollapse,
      queryType?: ISearchParams['query_type']
    ) => {
      const isOverLimit = await checkFinderSearchIsOverLimit(dataType)

      if (isOverLimit) {
        return
      }

      const query: IQuery = {
        type: 'group',
        must,
      }
      const config = RouteConfig[dataType]
      if (!config) return
      const queryId = await getQueryIdByQuery(query, dataType, config.fromModule, collapse, undefined, options?.customSortFn?.(query), queryType)
      if (queryId) {
        trackingEvent('ACTIVE_SEARCH', {
          common1: dataType.toUpperCase(),
          payload: {
            query,
            query_type: queryType,
            data_type: dataType,
          },
        })
        // 这边的事件会在quickSearch中消费
        ins?.proxy.$emit('previewSearch')
        const tab = ins?.proxy.$route.query[E_ROUTER_QUERY.TAB] || CommonListTabSetting.getSingleton().getTabType(dataType)
        const routerQuery = { [config.routeParam]: queryId }
        if (tab) {
          Object.assign(routerQuery, { [E_ROUTER_QUERY.TAB]: tab })
        }
        if (dataType === 'patent') {
          Object.assign(routerQuery, { [E_ROUTER_QUERY.PATENT_COLLAPSE_MODE]: 'user_setting' })
        }
        ins?.proxy.$router.push({ name: config.route, query: routerQuery })

        handleUpdateRights(queryId)
      }
    },
    handleSelectTag: <T extends DataTagKey>(params: {
      data: { [key in T]: IBAcTag[] }
      externalData?: { totalRegulationItems?: IBAcTag[]; therapeuticAreasDataItems?: IBAcTag[]; drugPatentTypeTags?: IBAcTag[] }
      key: T
      item: IBAcTag
      limit?: number
    }) => {
      const { data, externalData = {}, key, item, limit } = params
      if (item.id === 'All') {
        data[key] = [generateAllTag()]
      } else {
        const isExist = checkExist(data[key], item, isCN.value)
        if (isExist) {
          data[key] = data[key].filter((o) => o.id !== item.id)
        } else {
          data[key].push(item)
        }
        data[key] = data[key].filter((o) => o.id !== 'All')
        if (!data[key].length) {
          data[key] = [generateAllTag()]
        }
        if (limit && data[key].length > limit) {
          data[key] = data[key].slice(-limit)
        }
        const totalLength = action.getTotalTagLengthByKey<T>(externalData, key)
        const hasSelected = data[key].length
        if (hasSelected === totalLength - 1) {
          data[key] = [generateAllTag()]
        }
      }
    },
    getTotalTagLengthByKey: <T extends DataTagKey>(
      externalData: { totalRegulationItems?: IBAcTag[]; therapeuticAreasDataItems?: IBAcTag[]; drugPatentTypeTags?: IBAcTag[] },
      key: T
    ) => {
      if (key === 'regulationItems' && externalData.totalRegulationItems) {
        return externalData.totalRegulationItems.length
      }
      if (key === 'therapeuticAreasItems' && externalData.therapeuticAreasDataItems) {
        return externalData.therapeuticAreasDataItems.length
      }
      if (key === 'drugPatentTypeItems' && externalData.drugPatentTypeTags) {
        return externalData.drugPatentTypeTags.length
      }
      return (tagData[key] ?? []).length
    },
    convert2QueryItem: (params: {
      dataType: IFinderQueryType
      type: AllFinderFieldType
      items: IBAcTag[] | string[]
      fields: string[]
      condition?: E_QUERY_ITEM_CONDITION
      assigneeIncludeOriginal?: boolean
      isPrimaryEndpoint?: boolean
    }): IQueryItem => {
      const { dataType, type, items, fields, condition, assigneeIncludeOriginal, isPrimaryEndpoint } = params
      if (dataType === 'patent' && type === 'organization') {
        return {
          type: 'organization',
          condition,
          value: convertPatentOrg2QueryItem(items as IBAcTag[], false, assigneeIncludeOriginal),
        } as IQueryItemEntity
      }
      if (dataType === 'clinical_trial_result' && type === 'outcome') {
        return {
          type: 'outcome',
          condition,
          value: convertClinicalResultOutcome2QueryItem(items as IBAcTag[], isPrimaryEndpoint),
        } as IQueryItemCustomEntity
      }
      return {
        type: 'field',
        fields,
        condition: condition || E_QUERY_ITEM_CONDITION.ANY,
        value: ConvertValueConfig[dataType].convertValue(items, type, locale.value, assigneeIncludeOriginal, isPrimaryEndpoint),
      }
    },
    regenerateSearchQueryItems(searchQuery: IQueryItem, fields: string[]) {
      if (searchQuery.type === 'field') {
        searchQuery.fields = fields
      }
      if (searchQuery.type === 'group') {
        searchQuery.fields = fields
        if ('must' in searchQuery) {
          searchQuery.must?.forEach((item) => {
            action.regenerateSearchQueryItems(item, fields)
          })
        }
        if ('any' in searchQuery) {
          searchQuery.any?.forEach((item) => {
            action.regenerateSearchQueryItems(item, fields)
          })
        }
      }
    },
    generateQueryItems: (params: {
      dataType: IFinderQueryType
      queryItems: IQueryItem[]
      items: IBAcTag[] | string[]
      fields: string[]
      key: string
      type: AllFinderFieldType
      assigneeIncludeOriginal?: boolean
      isPrimaryEndpoint?: boolean
    }) => {
      const { dataType, queryItems, items, fields, key, type, assigneeIncludeOriginal, isPrimaryEndpoint } = params
      if (items.some((item) => isStringQueryItem(item))) {
        items.forEach((item) => {
          if (typeof item !== 'string' && 'searchQuery' in item && item.searchQuery) {
            if (item.searchQuery.type === 'field' || item.searchQuery.type === 'group') {
              action.regenerateSearchQueryItems(item.searchQuery, fields)
              queryItems.push({
                ...item.searchQuery,
                fields,
              })
            }
          }
        })
      } else {
        const queryItem = action.convert2QueryItem({
          dataType,
          type,
          items,
          fields,
          condition: logicConfig[key as ILogicKey] || E_QUERY_ITEM_CONDITION.ANY,
          assigneeIncludeOriginal,
          isPrimaryEndpoint,
        })
        queryItems.push(queryItem)
      }
    },
    getSyntaxTreeByStr: (data: any, key: 'allFieldItems', str: string, fields?: string[]) => {
      const syntaxTree = TokenUtil.getInstance().getSyntaxTree(formatStr(str))
      action.handleEditQueryConfirm(
        data,
        key,
        {
          query: SyntaxTreeRecognize.getInstance().trans2QueryItem(syntaxTree) as IQuery,
          source: str,
        },
        fields
      )
    },
  }
  return {
    state: {
      logicConfig,
    },
    action,
  }
}
